import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';

function SamplesTable({ title, samples }) {
  if (!samples?.length) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{title}</p>
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-2 py-1 font-medium">Invoice</th>
              <th className="text-left px-2 py-1 font-medium">Was</th>
              <th className="text-left px-2 py-1 font-medium">Resolved to</th>
            </tr>
          </thead>
          <tbody>
            {samples.map((s, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-2 py-1 font-mono">{s.invoice_id || '-'}</td>
                <td className="px-2 py-1">{s.current_supplier_client_name || '(empty)'}</td>
                <td className="px-2 py-1">{s.resolved_name || '(unresolved)'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PopulationReconciliationSection({ recon, loading, error, onRun }) {
  return (
    <div className="border-t pt-3 mt-3 space-y-2" data-testid="pennylane-cp-population-reconciliation">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">Population reconciliation</p>
        <Button
          variant="outline"
          size="sm"
          onClick={onRun}
          disabled={loading}
          data-testid="pennylane-cp-population-reconciliation-run"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Run reconciliation
        </Button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {recon && (
        <div className="space-y-3">
          <div className={`text-xs p-2 rounded border ${recon.reconciles ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            Local Unknown total: <strong>{recon.total_local_unknown}</strong>{' '}
            vs accounted: <strong>{recon.accounted_total}</strong>{' '}
            (<span data-testid="pennylane-cp-population-reconciliation-status">
              {recon.reconciles ? 'reconciles' : 'MISMATCH'}
            </span>)
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">
              Local docs not re-fetchable: {recon.not_refetchable_total}
            </p>
            {recon.not_refetchable_by_credential?.length > 0 && (
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1 font-medium">Credential</th>
                      <th className="text-left px-2 py-1 font-medium">Entity</th>
                      <th className="text-left px-2 py-1 font-medium">Status</th>
                      <th className="text-left px-2 py-1 font-medium">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recon.not_refetchable_by_credential.map((g, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{g.credential_label}</td>
                        <td className="px-2 py-1">{g.entity_id || '-'}</td>
                        <td className="px-2 py-1">{g.status}</td>
                        <td className="px-2 py-1">{g.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">
              API docs not local (not found locally): {recon.not_found_locally_total}, sampled {recon.not_found_locally_sample_size}
            </p>
            {recon.not_found_locally_samples_explained?.length > 0 && (
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1 font-medium">External id</th>
                      <th className="text-left px-2 py-1 font-medium">Type</th>
                      <th className="text-left px-2 py-1 font-medium">Date</th>
                      <th className="text-left px-2 py-1 font-medium">Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recon.not_found_locally_samples_explained.map((s, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{s.external_id}</td>
                        <td className="px-2 py-1">{s.type}</td>
                        <td className="px-2 py-1">{s.date || '-'}</td>
                        <td className={`px-2 py-1 ${s.explained_by ? '' : 'text-amber-600'}`}>{s.explanation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PennylaneCounterpartyBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState(null);

  const runReconciliation = async () => {
    setReconLoading(true);
    setReconError(null);
    try {
      const res = await diagnosticsAPI.pennylaneCounterpartyBackfillPopulationReconciliation();
      setRecon(res.data);
    } catch {
      setReconError('Failed to run population reconciliation.');
    } finally {
      setReconLoading(false);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await diagnosticsAPI.pennylaneCounterpartyBackfillPreview();
      setPreview(res.data);
    } catch {
      setPreview({ error: 'Failed to run preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const totalProposed = (preview?.resolved_count || 0) + (preview?.object_missing_count || 0);

  const runApply = async () => {
    if (!window.confirm(
      `Resolve supplier_client_name for ${totalProposed} invoices ` +
      `(${preview?.unresolved_count || 0} will stay unresolved and tagged)? ` +
      'Live re-fetch, conditional write, idempotent.'
    )) return;
    setApplyLoading(true);
    try {
      const res = await diagnosticsAPI.pennylaneCounterpartyBackfillApply();
      setApplyResult(res.data);
    } catch {
      setApplyResult({ error: 'Failed to apply backfill.' });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Pennylane Counterparty Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Resolves supplier_client_name for Pennylane invoices/expenses still
          carrying "" or "Unknown" (all 1,065 pre-fix docs per the census).
          Live re-fetch per active credential, resolved via the same directory
          lookup the sync now uses. Two-step: Preview, then Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading}>
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && totalProposed > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply {totalProposed} Corrections
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>Resolved: <strong className="text-green-600">{preview.resolved_count}</strong></span>
                  <span>Object missing (Unknown): <strong>{preview.object_missing_count}</strong></span>
                  <span>Unresolved (id, no name): <strong className="text-amber-600">{preview.unresolved_count}</strong></span>
                  <span>Already named: {preview.already_named_count}</span>
                  {preview.not_found_locally > 0 && (
                    <span>Not found locally: {preview.not_found_locally}</span>
                  )}
                </div>

                {preview.fetch_errors?.length > 0 && (
                  <div className="p-2 rounded bg-red-50 border border-red-200 text-xs text-red-600">
                    {preview.fetch_errors.length} credential(s) failed to fetch:{' '}
                    {preview.fetch_errors.map((e) => e.entity_id || e.credential_id).join(', ')}
                  </div>
                )}

                <SamplesTable title="Resolved (samples)" samples={preview.resolved_samples} />
                <SamplesTable title="Unresolved (samples)" samples={preview.unresolved_samples} />
                <SamplesTable title="Object missing (samples)" samples={preview.object_missing_samples} />
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Applied:</strong> {applyResult.written} invoices corrected, {applyResult.skipped_conflict} skipped (conflict)</p>
                {applyResult.written_samples?.length > 0 && (
                  <div className="mt-1">
                    <span className="font-medium">Samples:</span>
                    {applyResult.written_samples.map((s, i) => (
                      <span key={i} className="ml-1 font-mono">
                        {s.invoice_id}: {s.resolved_name || '(unresolved)'}
                        {i < applyResult.written_samples.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <PopulationReconciliationSection
          recon={recon}
          loading={reconLoading}
          error={reconError}
          onRun={runReconciliation}
        />
      </CardContent>
    </Card>
  );
}
